import { useState } from 'react';
import { Plus, Trash2, Edit2, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useCategoryBudgets, CategoryBudget, CreateBudgetInput } from '@/hooks/useCategoryBudgets';
import { formatCurrency } from '@/lib/foco-calculations';
import { useProfile } from '@/hooks/useProfile';

const variableExpenseCategories = [
  { value: 'food', label: 'Alimentación' },
  { value: 'transport', label: 'Transporte' },
  { value: 'entertainment', label: 'Entretenimiento' },
  { value: 'health', label: 'Salud' },
  { value: 'education', label: 'Educación' },
  { value: 'clothing', label: 'Ropa' },
  { value: 'personal_care', label: 'Cuidado personal' },
  { value: 'gifts', label: 'Regalos' },
  { value: 'subscriptions', label: 'Suscripciones' },
  { value: 'supermercado', label: 'Supermercado' },
  { value: 'restaurantes', label: 'Restaurantes' },
  { value: 'belleza', label: 'Belleza' },
  { value: 'mascotas', label: 'Mascotas' },
  { value: 'hogar', label: 'Hogar' },
  { value: 'other', label: 'Otros' },
];

export function BudgetManager() {
  const { budgets, createBudget, updateBudget, deleteBudget, isLoading } = useCategoryBudgets();
  const { profile } = useProfile();
  const currency = profile?.default_currency || 'CRC';
  
  const [isOpen, setIsOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<CategoryBudget | null>(null);
  const [category, setCategory] = useState('');
  const [monthlyLimit, setMonthlyLimit] = useState('');

  const existingCategories = budgets.map(b => b.category.toLowerCase());
  const availableCategories = variableExpenseCategories.filter(
    c => !existingCategories.includes(c.value) || editingBudget?.category.toLowerCase() === c.value
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const input: CreateBudgetInput = {
      category,
      monthly_limit: parseFloat(monthlyLimit),
    };

    if (editingBudget) {
      await updateBudget.mutateAsync({ id: editingBudget.id, ...input });
    } else {
      await createBudget.mutateAsync(input);
    }

    resetForm();
  };

  const resetForm = () => {
    setIsOpen(false);
    setEditingBudget(null);
    setCategory('');
    setMonthlyLimit('');
  };

  const openEdit = (budget: CategoryBudget) => {
    setEditingBudget(budget);
    setCategory(budget.category);
    setMonthlyLimit(budget.monthly_limit.toString());
    setIsOpen(true);
  };

  const getCategoryLabel = (value: string) => {
    const cat = variableExpenseCategories.find(c => c.value.toLowerCase() === value.toLowerCase());
    return cat?.label || value;
  };

  return (
    <Card className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">Presupuestos por Categoría</h3>
        </div>
        
        <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Agregar
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingBudget ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select value={category} onValueChange={setCategory} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCategories.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Límite Mensual ({currency})</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={monthlyLimit}
                  onChange={(e) => setMonthlyLimit(e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={createBudget.isPending || updateBudget.isPending}>
                {editingBudget ? 'Guardar Cambios' : 'Crear Presupuesto'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando...</p>
      ) : budgets.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No tienes presupuestos configurados. Agrega uno para recibir alertas cuando te acerques al límite.
        </p>
      ) : (
        <div className="space-y-3">
          {budgets.map((budget) => (
            <div key={budget.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Switch
                  checked={budget.is_active}
                  onCheckedChange={(checked) => updateBudget.mutate({ id: budget.id, is_active: checked })}
                />
                <div>
                  <p className={`font-medium ${!budget.is_active ? 'text-muted-foreground' : ''}`}>
                    {getCategoryLabel(budget.category)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatCurrency(budget.monthly_limit, currency)} / mes
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="icon" onClick={() => openEdit(budget)}>
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => deleteBudget.mutate(budget.id)}
                  disabled={deleteBudget.isPending}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
